import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Grid,
  Modal,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import { useMemo, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useParams } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import CondensedTable from 'components/CondensedTable/CondensedTable';
import DisplayValue from 'components/DisplayValue/DisplayValue';
import AlertTooltip from 'components/AlertTooltip/AlertTooltip';
import SectionMessage from 'components/SectionMessage/SectionMessage';
import SimpleGrid from 'components/SimpleGrid/SimpleGrid';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import Pill from 'components/Pill/Pill';
import useTimeoutValue from 'hooks/useTimeoutValue';
import { SIZE, STATUS } from 'utilities/constants';
import { DIGITAL_WALLET_INFO_QUERY } from 'queries/wallet';
import { UPDATE_WALLET } from 'mutations/wallet';

const provisioningHistoryColumns = Object.freeze([
  { title: 'Wallet Type', dataIndex: 'vendor' },
  { title: 'Date', dataIndex: 'date' },
  { title: 'Time', dataIndex: 'time' },
  { title: 'Token Number', dataIndex: 'tokenNumber' },
  { title: 'Token Requestor ID', dataIndex: 'tokenRequesterId' },
  {
    title: 'Reason',
    dataIndex: 'reason',
  },
  { title: 'Indicator', dataIndex: 'indicator' },
  {
    title: 'DNITHE',
    dataIndex: 'dnitheId',
  },
]);

const DigitalWalletModal = ({ open, ...modalProps }) => {
  const { accountId, customerId } = useParams();
  const { showDigitalWalletManagement } = useFlags();

  const [getDigitalWalletInfo, digitalWalletInfoQuery] = useLazyQuery(
    DIGITAL_WALLET_INFO_QUERY,
  );

  const [wallets, walletEvents, eligible, ineligibilityReasons] =
    useMemo(() => {
      const digitalWallet = digitalWalletInfoQuery.data?.account?.digitalWallet;
      const eligibility =
        digitalWalletInfoQuery.data?.account?.checkEligibility?.eligibility ||
        {};

      let wallets = digitalWallet?.info?.wallets || [];
      let events = digitalWallet?.events || [];

      wallets = wallets.map((wallet) => {
        const key = `digital-wallet-${wallet.dnitheId}`;
        return {
          ...wallet,
          key,
        };
      });

      events = events.map((event, index) => {
        const key = `digital-wallet-event-${index}`;
        const dnitheId =
          wallets?.find(
            (wallet) =>
              wallet.tokenRequestorId === event?.tokenRequesterId &&
              wallet.tokenNumber === event?.tokenNumber,
          )?.dnitheId || '';
        return {
          ...event,
          key,
          dnitheId,
        };
      });

      const eligible = eligibility.eligible !== false;
      let ineligibilityReasons = [];

      if (!eligible && eligibility?.reasons?.length) {
        ineligibilityReasons = eligibility.reasons.filter(
          (reason) => !reason.pass,
        );
      }

      return [wallets, events, eligible, ineligibilityReasons];
    }, [digitalWalletInfoQuery.data]);

  useEffect(() => {
    if (open) {
      getDigitalWalletInfo({
        variables: { accountId, customerId },
      });
    }
  }, [customerId, accountId, open]);

  return (
    <Modal
      {...modalProps}
      centered
      width={1200}
      cancelButtonProps={{ hidden: true }}
      title={
        showDigitalWalletManagement
          ? 'Manage Digital Wallet Status'
          : 'Provisioning History'
      }
      open={open}
    >
      <QueryBoundary
        query={digitalWalletInfoQuery}
        mode={QueryBoundary.MODE.MESSAGE}
        loadingMessage="Loading digital wallet information."
        errorMessage="Failed to load digital wallet information."
      >
        {showDigitalWalletManagement && (
          <>
            <Typography.Paragraph strong>Wallets</Typography.Paragraph>
            {!eligible && (
              <Alert
                banner
                type="warning"
                style={{ marginBottom: 'var(--spacing-sm)' }}
                title={
                  <>
                    <div>
                      Account is ineligible for digital wallet activation due to
                      the following check failures
                    </div>
                    <ul style={{ marginBottom: 0 }}>
                      {ineligibilityReasons.map((reason) => (
                        <li key={reason.code}>{reason.description}</li>
                      ))}
                    </ul>
                  </>
                }
              />
            )}
            {wallets.length ? (
              <SimpleGrid
                style={{
                  gridGap: 'var(--spacing-sm) var(--spacing-sm)',
                  marginBottom: 'var(--spacing-lg)',
                }}
              >
                {wallets.map((wallet) => {
                  return (
                    <Wallet
                      accountId={accountId}
                      wallet={wallet}
                      eligibleForActivation={eligible}
                      key={wallet.key}
                    />
                  );
                })}
              </SimpleGrid>
            ) : (
              <SectionMessage
                size={SIZE.SM}
                status={STATUS.INFORMATION}
                text="There are currently no wallets provisioned for this account"
              ></SectionMessage>
            )}
            <Typography.Paragraph strong>
              Provisioning History
            </Typography.Paragraph>
          </>
        )}

        <CondensedTable
          data={walletEvents}
          columns={provisioningHistoryColumns}
          bordered={true}
          length={10}
        ></CondensedTable>
      </QueryBoundary>
    </Modal>
  );
};

export const WALLET_OPERATION = {
  ACTIVATE: 'ACTIVATE',
  DELETE: 'DELETE',
};

export const WALLET_OPERATION_REASON = {
  ACCOUNT_CLOSED: 'ACCOUNT_CLOSED',
  CONSUMER_DELETED: 'CONSUMER_DELETED',
  CUSTOMER_REQUEST: 'CUSTOMER_REQUEST',
  DEVICE_LOST: 'DEVICE_LOST',
  DEVICE_STOLEN: 'DEVICE_STOLEN',
  FRAUD_TRANSACTION: 'FRAUD_TRANSACTION',
  OTHER: 'OTHER',
};

export const WALLET_AUTHENTICATION_METHOD = {
  AUTHENTICATED_BY_CALL_CENTER: 'AUTHENTICATED_BY_CALL_CENTER',
};

const DELETION_REASONS = [
  {
    value: WALLET_OPERATION_REASON.ACCOUNT_CLOSED,
    label: 'Account Closed',
  },
  {
    value: WALLET_OPERATION_REASON.CONSUMER_DELETED,
    label: 'Consumer Deleted',
  },
  {
    value: WALLET_OPERATION_REASON.DEVICE_LOST,
    label: 'Device Lost',
  },
  {
    value: WALLET_OPERATION_REASON.DEVICE_STOLEN,
    label: 'Device Stolen',
  },
  {
    value: WALLET_OPERATION_REASON.FRAUD_TRANSACTION,
    label: 'Fraudulent Transaction',
  },
  {
    value: WALLET_OPERATION_REASON.OTHER,
    label: 'Other',
  },
];

const Wallet = ({ accountId, wallet, eligibleForActivation }) => {
  const { enableDigitalWalletActivation } = useFlags();
  const breakpoints = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const [operationType, setOperationType] = useState();
  const [showFailure, setShowFailure] = useTimeoutValue(false);

  const active = wallet.status === 'Active';

  const [updateWallet, updateWalletMutation] = useMutation(UPDATE_WALLET, {
    refetchQueries: (response) => {
      if (response.data?.updateWalletToken?.success) {
        return ['DigitalWalletInfo'];
      }
    },
  });
  const performingOperation = updateWalletMutation.loading;

  const handleOperation = async () => {
    setShowFailure(false);

    try {
      await form.validateFields();
    } catch {
      return;
    }

    const activating = operationType === WALLET_OPERATION.ACTIVATE;

    //the only activate reason that fits an agent activating with the cardholder on the line
    let reasonCode = WALLET_OPERATION_REASON.CUSTOMER_REQUEST;

    if (operationType === WALLET_OPERATION.DELETE) {
      reasonCode = form.getFieldValue('reasonCode');
    }

    try {
      const response = await updateWallet({
        variables: {
          accountId,
          comment: '',
          operationType,
          reasonCode,
          tokenId: wallet.dnitheId,
          walletTypeId: wallet.tokenRequestorId,
          //cc-accounts rejects this field on anything but an activation
          ...(activating && {
            authenticationMethod:
              WALLET_AUTHENTICATION_METHOD.AUTHENTICATED_BY_CALL_CENTER,
          }),
        },
      });
      const success = response.data.updateWalletToken.success;
      if (!success) {
        setShowFailure(true);
        return;
      }
      setOperationType(undefined);
    } catch (error) {
      setShowFailure(true);
    }
  };

  const renderActivate = () => {
    return (
      <>
        <Typography.Paragraph>
          Are you sure you want to activate this wallet?
        </Typography.Paragraph>
        <Flex gap="var(--spacing-sm)">
          <Button
            size="small"
            disabled={performingOperation}
            onClick={() => setOperationType(undefined)}
          >
            Cancel
          </Button>
          <AlertTooltip
            type="error"
            placement="top"
            open={showFailure}
            title="Failed to activate wallet."
          >
            <Button
              data-testid="submit-activate"
              size="small"
              type="primary"
              loading={performingOperation}
              onClick={handleOperation}
            >
              Activate
            </Button>
          </AlertTooltip>
        </Flex>
      </>
    );
  };

  const renderDelete = () => {
    return (
      <div style={{ marginBottom: 'var(--spacing-xs)' }}>
        <Form.Item
          name="reasonCode"
          label="Select a reason for deletion"
          size="small"
          rules={[{ required: true, message: 'Please select a reason' }]}
        >
          <Select size="small" options={DELETION_REASONS}></Select>
        </Form.Item>

        <Flex gap="var(--spacing-sm)" justify="center">
          <Button
            size="small"
            disabled={performingOperation}
            onClick={() => setOperationType(undefined)}
          >
            Cancel
          </Button>

          <AlertTooltip
            type="error"
            placement="top"
            open={showFailure}
            title="Failed to delete wallet."
          >
            <Button
              data-testid="submit-delete"
              size="small"
              type="primary"
              danger
              loading={performingOperation}
              onClick={handleOperation}
            >
              Delete
            </Button>
          </AlertTooltip>
        </Flex>
      </div>
    );
  };

  return (
    <Card
      data-testid={`wallet-${wallet.dnitheId}`}
      title={wallet.dnitheId}
      size="small"
      style={{
        gridColumn: breakpoints.lg ? 'span 4' : 'span 6',
      }}
      extra={
        <Pill data-testid="status-pill" color={active ? 'green' : undefined}>
          {wallet.status}
        </Pill>
      }
    >
      <div style={{ position: 'relative' }}>
        <Typography.Paragraph>{wallet.description}</Typography.Paragraph>
        <SimpleGrid
          style={{
            gridGap: 'var(--spacing-sm) var(--spacing-sm)',
            marginBottom: 'var(--spacing-lg)',
          }}
        >
          <DisplayValue label="Token" style={{ gridColumn: 'span 6' }}>
            {wallet.tokenNumber}
          </DisplayValue>
          <DisplayValue
            label="Token Requestor ID"
            style={{ gridColumn: 'span 6' }}
          >
            {wallet.tokenRequestorId}
          </DisplayValue>
        </SimpleGrid>
        <Flex gap="var(--spacing-xs)" style={{ gridColumn: 'span 12' }}>
          {!active ? (
            <Tooltip
              title={
                enableDigitalWalletActivation
                  ? ''
                  : 'Activation is currently unavailable'
              }
            >
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={
                  !enableDigitalWalletActivation || !eligibleForActivation
                }
                onClick={() => {
                  setOperationType(WALLET_OPERATION.ACTIVATE);
                }}
              >
                Activate
              </Button>
            </Tooltip>
          ) : undefined}
          <Button
            size="small"
            variant="outlined"
            color="danger"
            onClick={() => {
              setOperationType(WALLET_OPERATION.DELETE);
            }}
          >
            Delete
          </Button>
        </Flex>

        <Form form={form} layout="vertical">
          {operationType ? (
            <Flex
              align="center"
              justify="center"
              vertical
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                left: 0,
                bottom: '-2px', //awkward, but it covers the bottom shadow of colored buttons
                paddingBottom: '2px',
                backgroundColor: 'var(--color-background)',
              }}
            >
              {operationType === WALLET_OPERATION.ACTIVATE && renderActivate()}
              {operationType === WALLET_OPERATION.DELETE && renderDelete()}
            </Flex>
          ) : undefined}
        </Form>
      </div>
    </Card>
  );
};

export default DigitalWalletModal;
